// src/scripts/sqlApiHelper/stockOptionsApi.js
// Persistent option lists used by the Stock Database.

import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { auth, db } from "@/firebase";

const OPTIONS_COLLECTION = "stock_reference_data";
const OPTIONS_DOCUMENT = "dropdown_options";

/*
 * These are dropdown OPTIONS only.
 *
 * They are NOT conversion values.
 * All actual conversion values are entered against each individual
 * stock item in the Stock Database.
 */
const DEFAULT_ORDER_UNITS = [
  "Btl",
  "BIB",
  "Case",
  "Keg",
  "Box",
  "Bag",
  "Unit",
];

const DEFAULT_STOCK_UNITS = [
  "Btl",
  "Ltr",
  "Pint",
  "Can",
  "Unit",
  "Gallon",
];

const DEFAULT_PRODUCT_GROUPS = [
  "Draught Beer",
  "Bottled Beer",
  "FaB's",
  "Vodka",
  "Rum",
  "Gin",
  "Whisky",
  "Brandy",
  "Tequila",
  "Liqueurs",
  "Red Wine",
  "White Wine",
  "Rose Wine",
  "Champagnes",
  "Draught Post Mix",
  "Soft Drinks",
];

export const DEFAULT_STOCK_OPTIONS = Object.freeze({
  orderUnits: Object.freeze([
    ...DEFAULT_ORDER_UNITS,
  ]),

  stockUnits: Object.freeze([
    ...DEFAULT_STOCK_UNITS,
  ]),

  suppliers: Object.freeze([]),

  productGroups: Object.freeze([
    ...DEFAULT_PRODUCT_GROUPS,
  ]),
});

function requireSignedInUser() {
  const user = auth.currentUser;

  if (!user) {
    throw new Error(
      "You must be signed in to manage stock options."
    );
  }

  return user;
}

function cleanList(values) {
  const cleaned = [];
  const seen = new Set();

  for (
    const rawValue of Array.isArray(values)
      ? values
      : []
  ) {
    const value = String(rawValue ?? "")
      .trim()
      .replace(/\s+/g, " ");

    const key =
      value.toLocaleLowerCase("en-GB");

    if (
      value &&
      !seen.has(key)
    ) {
      seen.add(key);
      cleaned.push(value);
    }
  }

  return cleaned;
}

function mergeLists(...lists) {
  return cleanList(
    lists.flatMap((list) =>
      Array.isArray(list) ? list : []
    )
  );
}

function firstNonEmptyList(...lists) {
  for (const list of lists) {
    const cleaned = cleanList(list);

    if (cleaned.length) {
      return cleaned;
    }
  }

  return [];
}

function normaliseOptions(
  data = {},
  useDefaultsForMissingFields = false
) {
  /*
   * Legacy support:
   *
   * The previous Stock Database used one "Unit Size" list.
   * If the new Order Unit / Stock Unit lists have not yet been
   * created, those old unit sizes are included temporarily so
   * existing data remains editable.
   */
  const legacyUnitSizes = mergeLists(
    data.unitSizes,
    data.unit_sizes
  );

  let orderUnits = firstNonEmptyList(
    data.orderUnits,
    data.order_units
  );

  let stockUnits = firstNonEmptyList(
    data.stockUnits,
    data.stock_units
  );

  if (!orderUnits.length) {
    orderUnits = legacyUnitSizes;
  }

  if (!stockUnits.length) {
    stockUnits = legacyUnitSizes;
  }

  if (
    useDefaultsForMissingFields &&
    !orderUnits.length
  ) {
    orderUnits = [
      ...DEFAULT_STOCK_OPTIONS.orderUnits,
    ];
  }

  if (
    useDefaultsForMissingFields &&
    !stockUnits.length
  ) {
    stockUnits = [
      ...DEFAULT_STOCK_OPTIONS.stockUnits,
    ];
  }

  let productGroups = firstNonEmptyList(
    data.productGroups,
    data.product_groups
  );

  if (
    useDefaultsForMissingFields &&
    !productGroups.length
  ) {
    productGroups = [
      ...DEFAULT_STOCK_OPTIONS.productGroups,
    ];
  }

  return {
    orderUnits,

    stockUnits,

    suppliers: cleanList(
      data.suppliers
    ),

    productGroups,
  };
}

function defaultOptions() {
  return {
    orderUnits: [
      ...DEFAULT_STOCK_OPTIONS.orderUnits,
    ],

    stockUnits: [
      ...DEFAULT_STOCK_OPTIONS.stockUnits,
    ],

    suppliers: [
      ...DEFAULT_STOCK_OPTIONS.suppliers,
    ],

    productGroups: [
      ...DEFAULT_STOCK_OPTIONS.productGroups,
    ],
  };
}

function optionsRef() {
  return doc(
    db,
    OPTIONS_COLLECTION,
    OPTIONS_DOCUMENT
  );
}

export async function fetchStockOptions() {
  requireSignedInUser();

  const snapshot =
    await getDoc(
      optionsRef()
    );

  if (!snapshot.exists()) {
    return defaultOptions();
  }

  return normaliseOptions(
    snapshot.data(),
    true
  );
}

export async function saveStockOptions(
  options
) {
  const user =
    requireSignedInUser();

  const payload =
    normaliseOptions(options);

  /*
   * unitSizes is retained as a compatibility field for any
   * older part of CoreDeck which still reads the old option list.
   *
   * It is NOT used for the new conversion calculations.
   */
  const legacyUnitSizes =
    mergeLists(
      payload.orderUnits,
      payload.stockUnits
    );

  await setDoc(
    optionsRef(),
    {
      orderUnits:
        payload.orderUnits,

      stockUnits:
        payload.stockUnits,

      suppliers:
        payload.suppliers,

      productGroups:
        payload.productGroups,

      unitSizes:
        legacyUnitSizes,

      updatedAt:
        serverTimestamp(),

      updatedBy:
        user.uid,
    },
    {
      merge: true,
    }
  );

  return payload;
}